// Excel-like expression evaluator for the embed grid's column totals.
//
// Design:
//   - Bare column db_name → SUM of that column across visible rows.
//   - <col>_sum / _avg / _min / _max / _count → explicit aggregates.
//   - hot-formula-parser handles the rest: + - * / ^ % comparisons,
//     parens, plus built-ins (IF, IFERROR, ROUND, ABS, AVERAGE, MIN,
//     MAX, COUNT, AND/OR/NOT, text fns, etc.).
//   - SUMPRODUCT / row-wise aggregation NOT supported in v1 — flagged
//     in the editor.

import { Parser } from 'hot-formula-parser'

export interface ColumnAggregates {
  sum: number
  avg: number
  min: number
  max: number
  count: number
}

const ZERO_AGG: ColumnAggregates = { sum: 0, avg: 0, min: 0, max: 0, count: 0 }

// Coerce an arbitrary cell value to a finite number, or null when the
// value isn't numeric. Strings get tried via Number(); booleans count
// as 0/1; empties / non-numeric strings are excluded so AVG of a sparse
// column reflects only the rows with data.
function coerceNumber(v: unknown): number | null {
  if (v == null || v === '') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'string') {
    const trimmed = v.trim()
    if (!trimmed) return null
    const n = Number(trimmed)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export function computeColumnAggregates(
  rows: Array<Record<string, unknown>>,
  column: string,
): ColumnAggregates {
  if (!rows || rows.length === 0) return { ...ZERO_AGG }
  let sum = 0
  let count = 0
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY
  for (const r of rows) {
    const n = coerceNumber(r?.[column])
    if (n === null) continue
    sum += n
    count += 1
    if (n < min) min = n
    if (n > max) max = n
  }
  if (count === 0) return { ...ZERO_AGG }
  return {
    sum,
    avg: sum / count,
    min,
    max,
    count,
  }
}

export interface EvaluateResult {
  ok: boolean
  // result is a number for arithmetic formulas, string for IF branches
  // that return text, boolean for comparison-only formulas. Caller
  // formats per-column.
  result: number | string | boolean | null
  error?: string
}

// Evaluate an Excel-like expression. `columns` lists every column
// db_name the formula may reference; each is exposed as four scalar
// variables: bare name (= sum), <name>_sum, <name>_avg, <name>_min,
// <name>_max, <name>_count.
export function evaluateExpression(
  expr: string,
  rows: Array<Record<string, unknown>>,
  columns: string[],
): EvaluateResult {
  if (!expr || !expr.trim()) return { ok: false, result: null, error: 'Empty formula' }
  const parser = new Parser()
  for (const col of columns) {
    const agg = computeColumnAggregates(rows, col)
    parser.setVariable(col, agg.sum)
    parser.setVariable(`${col}_sum`, agg.sum)
    parser.setVariable(`${col}_avg`, agg.avg)
    parser.setVariable(`${col}_min`, agg.min)
    parser.setVariable(`${col}_max`, agg.max)
    parser.setVariable(`${col}_count`, agg.count)
  }
  try {
    const res = parser.parse(expr) as { error: string | null; result: unknown }
    if (res.error) return { ok: false, result: null, error: res.error }
    const v = res.result
    if (v === null || v === undefined) {
      return { ok: false, result: null, error: 'No result' }
    }
    if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') {
      return { ok: true, result: v }
    }
    return { ok: true, result: String(v) }
  } catch (err) {
    return {
      ok: false,
      result: null,
      error: err instanceof Error ? err.message : 'Evaluation failed',
    }
  }
}

// Convenience: built-in aggregate over a single column. Used for the
// `kind: 'aggregate'` path that doesn't need the parser.
export function applyAggregate(
  rows: Array<Record<string, unknown>>,
  column: string,
  aggregate: 'sum' | 'avg' | 'min' | 'max' | 'count',
): number {
  const agg = computeColumnAggregates(rows, column)
  switch (aggregate) {
    case 'sum': return agg.sum
    case 'avg': return agg.avg
    case 'min': return agg.min
    case 'max': return agg.max
    case 'count': return agg.count
  }
}
